import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import AuthPage from "@/pages/AuthPage";
import AskPage from "@/pages/AskPage";
import AnswerPage from "@/pages/AnswerPage";
import ProfilePage from "@/pages/ProfilePage";
import DivePage from "@/pages/DivePage";
import EnjoyPage from "@/pages/EnjoyPage";
import HomePage from "@/pages/HomePage";
import { PublicShell } from "@/components/PublicShell";
import ShopPage from "@/pages/ShopPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import ConsolePage from "@/pages/ConsolePage";

const TOAST_OPTIONS = {
  style: {
    background: "#181818",
    border: "1px solid #2C2C2C",
    color: "#D4E6F1",
    fontFamily: "Courier Prime, Courier New, Courier, monospace",
  },
};

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_god) return <Navigate to="/console/overview" replace />;
  return <Layout>{children}</Layout>;
}

function MemberOnly() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_god) return <Navigate to="/home" replace />;
  return <ConsolePage />;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user && !user.is_god ? <Layout>{children}</Layout> : <PublicShell member={user?.is_god}>{children}</PublicShell>;
}

function Home({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return user.is_god ? <PublicShell member>{children}</PublicShell> : <Layout>{children}</Layout>;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={!user ? "/login" : user.is_god ? "/console/overview" : "/home"} replace />;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.is_god ? "/console/overview" : "/home"} replace />;
  return <AuthPage />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<Root />} />
            <Route path="/ask" element={<Protected><AskPage /></Protected>} />
            <Route path="/answer" element={<Protected><AnswerPage /></Protected>} />
            <Route path="/check" element={<Navigate to="/ask" replace />} />
            <Route path="/engage" element={<Navigate to="/ask" replace />} />
            <Route path="/absorb" element={<Navigate to="/ask" replace />} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/chat" element={<Protected><DivePage /></Protected>} />
            <Route path="/shop" element={<Protected><ShopPage /></Protected>} />
            <Route path="/challenge" element={<Protected><EnjoyPage /></Protected>} />
            <Route path="/extend" element={<Navigate to="/challenge" replace />} />
            <Route path="/mawilu" element={<Navigate to="/challenge" replace />} />
            <Route path="/enjoy" element={<Navigate to="/challenge" replace />} />
            <Route path="/console" element={<Navigate to="/console/overview" replace />} />
            <Route path="/console/:section" element={<MemberOnly />} />
            <Route path="/home" element={<Home><HomePage /></Home>} />
            <Route path="/logo" element={<Navigate to="/challenge" replace />} />
            <Route path="/payment/success" element={<Public><PaymentResultPage /></Public>} />
            <Route path="/payment/cancel" element={<Public><PaymentResultPage cancelled /></Public>} />
            <Route path="/dive" element={<Navigate to="/chat" replace />} />
            <Route path="*" element={<Root />} />
          </Routes>
          <Toaster
            theme="dark"
            position="bottom-center"
            toastOptions={TOAST_OPTIONS}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
